import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { LOGO_URL } from "../../components/brand";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const valid = pw.length >= 8 && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || !token) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users/set-password", { token, password: pw });
      localStorage.setItem("sac_token", data.token);
      setDone(true);
      toast.success("Password set — taking you to the admin portal");
      setTimeout(() => { window.location.href = "/admin"; }, 1200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set password — link may be invalid or expired");
    } finally { setBusy(false); }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center admin-bg p-6">
        <div className="admin-surface p-8 max-w-md text-center">
          <h1 className="font-display text-2xl text-[#5D2C8F] mb-2">Missing invite link</h1>
          <p className="text-sm text-[#6E6074]">This page requires a valid invite token. Please use the link from your invitation email or ask the workspace owner to re-send it.</p>
          <Link to="/admin/login" className="sac-btn-secondary mt-5 inline-flex">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen admin-bg flex items-center justify-center p-6" data-testid="set-password-page">
      <Toaster position="top-center" />
      <div className="admin-surface w-full max-w-md p-8">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="" className="h-14 w-auto mx-auto mb-3" />
          <div className="font-hand text-[#A685BD] text-2xl">welcome aboard</div>
          <h1 className="font-display text-3xl text-[#5D2C8F]">Set your password</h1>
          <p className="text-sm text-[#6E6074] mt-2">Choose a password (min 8 characters) to claim your admin account.</p>
        </div>

        {done ? (
          <div className="text-center py-8">
            <CheckCircle2 size={42} className="text-[#4FA07C] mx-auto mb-3" />
            <p className="text-[#5D2C8F]">All set! Redirecting to the admin portal…</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm text-[#5D2C8F]">New password</label>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2.5"
                placeholder="At least 8 characters"
                data-testid="setpw-password"
                autoFocus
              />
            </div>
            <div>
              <label className="text-sm text-[#5D2C8F]">Confirm password</label>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2.5"
                data-testid="setpw-confirm"
              />
              {pw2 && pw !== pw2 && <div className="text-xs text-[#A685BD] mt-1">Passwords don't match</div>}
            </div>
            <button
              type="submit"
              disabled={!valid || busy}
              className="sac-btn-primary w-full justify-center disabled:opacity-50"
              data-testid="setpw-submit"
            >
              <KeyRound size={14} /> {busy ? "Setting…" : "Set password & sign in"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
